import React, { useEffect, useMemo, useState } from 'react';
import { Button, Image, List, message, Typography, Tooltip, Divider, Tag, Upload } from 'antd';
import {
    DeleteOutlined,
    FileOutlined,
    CheckCircleTwoTone,
    LoadingOutlined,
    CheckOutlined,
    SyncOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import { convertToKB, isAllSuccess } from '../../exports/util';
import './upload-file.css';
import portalAPI from '../../api/api';
import { UPLOAD_CONTENT_URL } from '../../api/URLs';
import { imageCompressor } from '@mbs-dev/react-image-compressor';
import { bytesToMB, isImageType, MAX_FILE_SIZE_MB } from '../../exports/upload.util';

const { Dragger } = Upload;
const { Text } = Typography;

const UploadFile = ({ fieldKey, uploadedFiles = [], setClaimForm, setIsUploading, uploadType = 'multi', setIsNext }) => {
    const [fileList, setFileList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const isSingleUpload = uploadType === 'single';

    const disableUpload = useMemo(() => {
        if (isSingleUpload && fileList.length >= 1) {
            return true
        }

        if (fileList.length === 5) {
            return true
        }

    }, [isSingleUpload, fileList])

    const handleRemove = (uidToRemove) => {
        setClaimForm(prev => {
            if (fieldKey.startsWith('insuranceCards.')) {
                const sideToRemove = fieldKey.split('.')[1];
                const updatedInsuranceCards = (prev.insuranceCards || []).filter(item => {
                    return !(item.side === sideToRemove && item.file.id === uidToRemove);
                });
                return {
                    ...prev,
                    insuranceCards: updatedInsuranceCards,
                };
            } else {
                return {
                    ...prev,
                    [fieldKey]: (prev[fieldKey] || []).filter(f => f.id !== uidToRemove),
                };
            }
        });

        const newList = fileList.filter(file => file.uid !== uidToRemove);
        setFileList(newList);

        message.info('File removed.');
    };

    const renderPreview = (item) => {
        if (item?.type?.startsWith('image')) {
            return <Image src={item.url} width={100} />;
        }
        return (
            <Tooltip title="Click to view file">
                <a href={item.url} target="_blank" rel="noreferrer">
                    <FileOutlined style={{ fontSize: 20 }} /> {item?.name}
                </a>
            </Tooltip>
        );
    };

    const renderStatusIcon = (status) => {
        switch (status) {
            case "done":
                return <CheckCircleTwoTone twoToneColor="#52c41a" />;
            case "uploading":
                return <LoadingOutlined />;
            case "error":
                return <CloseCircleOutlined />;
            default:
                return null;
        }
    };

    const renderTagStatus = (status) => {
        switch (status) {
            case "done":
                return <Tag color="success">Completed</Tag>;
            case "uploading":
                return <Tag icon={<SyncOutlined spin />} color="processing">Uploading</Tag>;
            case "error":
                return <Tag icon={<CloseCircleOutlined />} color="error">Error</Tag>;
            default:
                return null;
        }
    };

    const uploadProps = {
        name: 'file',
        multiple: !isSingleUpload,
        maxCount: isSingleUpload ? 1 : 5,
        disabled: disableUpload,
        action: portalAPI.defaults.baseURL + UPLOAD_CONTENT_URL,
        showUploadList: false,
        accept: '.png,.jpg,.jpeg,.pdf,.doc,.docx',
        fileList,
        withCredentials: true,
        beforeUpload: async (file) => {
            const isImage = isImageType(file.type);
            let fileSize = bytesToMB(file.size);

            const isOver2MB = fileSize > MAX_FILE_SIZE_MB;

            let QUALITY = 1.2 / fileSize;

            if (isImage && isOver2MB) {
                try {
                    const compressedBlob = await imageCompressor(file, QUALITY);

                    return new File([compressedBlob], file.name, {
                        type: compressedBlob.type,
                        lastModified: Date.now(),
                    });
                } catch (error) {
                    console.error('Compression failed:', error);
                    message.error('Image compression failed');
                    return Upload.LIST_IGNORE;
                }
            }

            return file;
        },
        onChange(info) {
            const { file, fileList: newFileList } = info;
            const responseData = file.response;

            if (file.status === 'removed') return;

            if (file.status === 'uploading') {
                setIsLoading(true);
                setFileList(newFileList);
            }

            if (responseData?.status === 400) {
                const newList = fileList.filter(item => item.uid !== file.uid);
                setFileList(newList);
                setIsLoading(false);
                let errMessage = responseData?.message === "Uploaded file is too big" ? "Upload failed – maximum file size 5MB." : responseData.message;
                message.error(errMessage);
                return false;
            }

            if (file.status === 'done') {
                setIsLoading(false);

                const updatedFileList = newFileList.map((item) => {
                    if (item.uid === file.uid) {
                        return {
                            ...item,
                            status: responseData?.status !== 200 ? "error" : "done",
                            url: responseData?.data?.presignedUrl,
                            urlAccess: responseData?.data?.url,
                            contentDocumentId: responseData?.data?.contentDocumentId,
                        };
                    }
                    return item;
                });

                let fileData = {
                    id: Date.now() + Math.random(),
                    fileName: file.name,
                    s3Key: responseData?.data?.s3key,
                    fileType: file.type,
                    url: responseData.data.url,
                    presignedUrl: responseData.data.presignedUrl,
                    contentDocumentId: responseData?.data?.contentDocumentId,
                    size: file.size,
                };

                if (fieldKey.startsWith('insuranceCards.')) {
                    const side = fieldKey.split('.')[1];
                    setClaimForm(prev => ({
                        ...prev,
                        insuranceCards: [
                            ...(prev.insuranceCards || []),
                            { side, file: fileData }
                        ],
                    }));
                } else {
                    setClaimForm(prev => ({
                        ...prev,
                        [fieldKey]: [...(prev[fieldKey] || []), fileData],
                    }));
                }

                setFileList(updatedFileList);

                if (responseData?.status === 200) {
                    message.success(`${file.name} uploaded successfully.`);
                } else {
                    message.error(`${file.name} failed to upload.`);
                }
            }

            if (file.status === "error") {
                const updatedFileList = newFileList.map((item) => {
                    if (item.uid === file.uid) {
                        return {
                            ...item,
                            status: responseData?.status !== 200 ? "error" : "done",
                        };
                    }
                    return item;
                });

                setFileList(updatedFileList);
                setIsLoading(false);
                let errorMessage = responseData?.message || 'upload failed.'
                message.error(`${file.name} ${errorMessage}.`);
            }
        },
    };

    useEffect(() => {
        if (uploadedFiles && uploadedFiles.length) {
            const newFile = uploadedFiles.map((item) => ({
                uid: item.id || item._id,
                name: item.fileName,
                status: 'done',
                url: item.presignedUrl,
                urlAccess: item.url,
                type: item.fileType,
                size: item.size,
            }));
            setFileList([...newFile]);
        }
    }, [uploadedFiles]);

    const hasFiles = useMemo(() => fileList.length > 0, [fileList]);

    useEffect(() => {
        if (!Array.isArray(fileList)) return;

        const hasUploading = fileList.some(item => item?.status === "uploading");
        setIsUploading(hasUploading);

        const noFiles = fileList.length === 0;
        const notAllSuccess = !isAllSuccess(fileList);

        if (isLoading || noFiles || notAllSuccess) {
            setIsNext(false);
        } else {
            setIsNext(true);
        }
    }, [fileList, isLoading]);

    return (
        <div className='upload-portal'>
            <Dragger
                {...uploadProps}
                style={{ marginBottom: 16 }}
                className="custom-upload"
            >
                <Button className="upload-btn" icon={<CheckOutlined />} style={{ opacity: `${disableUpload ? '0.7' : '1'}` }} disabled={disableUpload}>
                    Choose
                </Button>
                <p className="ant-upload-hint">
                    Drag and drop files to here to upload.
                </p>
            </Dragger>

            {hasFiles && (
                <>
                    <Divider className='m-0' />
                    <List
                        className='uploaded-list'
                        dataSource={fileList}
                        renderItem={(item) => (
                            <List.Item
                                actions={[
                                    <Tooltip title="Remove file">
                                        <Button
                                            icon={<DeleteOutlined />}
                                            danger
                                            size="small"
                                            onClick={() => handleRemove(item.uid)}
                                        />
                                    </Tooltip>
                                ]}
                            >
                                <div className='row'>
                                    <div className='col-4'>{renderPreview(item)}</div>
                                    <div className='col-8'>
                                        <div className='d-flex flex-row align-items-center gap-3'>
                                            <span>{renderStatusIcon(item.status)}</span>
                                            <div>
                                                <div className='d-flex flex-row align-items-center gap-3'>
                                                    <div className='d-flex flex-column align-items-start'>
                                                        <Text className='file-name'>{item.name}</Text>
                                                        <Text>{item.size ? convertToKB(item.size) : 'N/A'}</Text>
                                                    </div>
                                                    {renderTagStatus(item.status)}
                                                </div>
                                                {
                                                    item.status === 'error' && (
                                                        <Tag bordered={false} color="error">
                                                            {item?.response?.message}
                                                        </Tag>
                                                    )
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </List.Item>
                        )}
                    />
                </>
            )}
        </div>
    );
};


export default UploadFile;